import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';
import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {CoachDetails, CoachesItem, CoachLite, EnabledStatus, ICoachBalance, ListResponse, LogsItem,} from '@/models';
import {changeEnabledStatus, http, makeCoachRequest} from '@/utils';

import {handleApiCallInStore} from './utils';
import {handleCoachFilesBeforeUpload} from "@/pages/coaches/create-or-edit/utils";

export interface ICreateOrUpdateCoachParams {
  coachId?: string;
  onSuccess: () => void;
  payload: CoachDetails;
}

export interface ICoachesStore {
  coachesList: CoachLite[];
  coachList: CoachesItem[];
  coachBalance: ICoachBalance;
  coachLogs: LogsItem[];
  coachBalanceLogs: LogsItem[];
  coachesListHitsNo: number;
  blockedCoachesListHitsNo: number;
  coachesListBlocked: CoachesItem[];
  coachInfo: CoachDetails;
  getCoachBalance: (id: string) => void;
  getCoachBalanceLogs: (id: string) => void;
  getCoachById: (id: string, onSuccess?: (data: CoachDetails) => void) => void;
  getAllCoachesLite: () => void;
  filterCoachList: (params) => void;
  createOrUpdateCoach: (data: ICreateOrUpdateCoachParams) => void;
  getCoachLogs: (coachId: string) => void;
  changeCoachStatus: (
    payload: EnabledStatus,
    listType: 'coaches' | 'blockedCoaches'
  ) => void;
}

export const useCoachesStore = create(
  persist<ICoachesStore>(
    set => ({
      coachBalanceLogs: [],
      coachBalance: undefined,
      coachesList: [],
      coachesListHitsNo: 0,
      blockedCoachesListHitsNo: 0,
      coachList: [],
      coachLogs: [],
      coachesListBlocked: [],
      coachInfo: undefined,
      getCoachBalanceLogs: async id => {
        handleApiCallInStore(LoadingKeys.CoachBalanceLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            API_ENDPOINTS.coaches.logs(id)
          );
          set(() => ({coachBalanceLogs: data?.items}));
        });
      },
      getCoachBalance: async id => {
        handleApiCallInStore(LoadingKeys.CoachBalance, async () => {
          const {data} = await http.get<ICoachBalance>(
            API_ENDPOINTS.coaches.balance(id)
          );
          set(() => ({coachBalance: data}));
        });
      },
      getAllCoachesLite: async () => {
        handleApiCallInStore(LoadingKeys.CoachesLite, async () => {
          const {data} = await http.get<{ items: CoachLite[] }>(
            API_ENDPOINTS.coaches.allLite
          );
          set(() => ({coachesList: data.items ?? []}));
        });
      },
      filterCoachList: async params => {
        handleApiCallInStore(LoadingKeys.GetCoachesList, async () => {
          const {key, ...otherParams} = params;
          const {data} = await http.get<ListResponse<CoachesItem>>(
            `${API_ENDPOINTS.coaches.all}?role=coach`,
            {params: otherParams} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() =>
            key === 'coaches'
              ? {
                coachList: items ?? [],
                coachesListHitsNo: nbHits ?? 0,
              }
              : {
                coachesListBlocked: items ?? [],
                blockedCoachesListHitsNo: nbHits ?? 0,
              }
          );
        });
      },
      createOrUpdateCoach: ({coachId, onSuccess, payload}) => {
        handleApiCallInStore(LoadingKeys.createOrUpdateCoach, async () => {
          const requestPayload = makeCoachRequest(payload);
          const finalPayload = await handleCoachFilesBeforeUpload(
            requestPayload,
            requestPayload?.mainImageIndex || 0
          );

          const apiLink = `${API_ENDPOINTS.coaches.all}/${
            coachId ? `${coachId}` : ''
          }`;
          if (coachId) {
            await http.put(apiLink, finalPayload);
          } else {
            await http.post(apiLink, finalPayload);
          }

          await onSuccess();
        });
      },
      changeCoachStatus: (payload, listType) => {
        handleApiCallInStore(LoadingKeys.ChangeCoachStatus, async () => {
          const {id, isEnabled} = payload;
          await http.put(`${API_ENDPOINTS.coaches.allLite}/${id}/deactivate`, {
            id,
            isEnabled,
          });
          const isBlockedList = listType === 'blockedCoaches';
          set(({coachesListBlocked, coachList}) => ({
            coachesListBlocked: isBlockedList
              ? coachesListBlocked.filter(
                coachItem => coachItem.objectID !== id
              )
              : coachesListBlocked,
            coachList: isBlockedList
              ? coachList
              : coachList.filter(coachItem => coachItem.objectID !== id),
          }));

          changeEnabledStatus(isEnabled, t('coach'));
        });
      },
      getCoachById: async (coachId, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetCoachById, async () => {
          const {data} = await http.get<CoachDetails>(
            `${API_ENDPOINTS.coaches.all}/${coachId}`
          );
          set(() => ({coachInfo: data}));
          if (onSuccess) {
            onSuccess(data);
          }
        });
      },
      getCoachLogs: coachId => {
        handleApiCallInStore(LoadingKeys.CoachLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            `${API_ENDPOINTS.logs}/coaches?id=${coachId}`
          );

          set(() => ({
            coachLogs: data?.items.filter(
              item => item.descriptiveData.id === coachId
            ),
          }));
        });
      },
    }),
    {
      name: 'coaches-storage',

    }
  )
);
